import { useState } from "react";
import { toast } from "sonner";
import API, { formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { fmtWhen } from "./InboxBits";
import { ComposerAssist } from "./ComposerAssist";
import { ReplyAttachments } from "./ReplyAttachments";
import { PaperPlaneTilt, ArrowBendUpLeft, X, Sparkle, ArrowsClockwise, Paperclip, CloudCheck } from "@phosphor-icons/react";

/** AI read: one-line summary + suggested category / urgency, with a re-run button. */
export function AiSummaryBox({ item, onChanged }) {
  const [busy, setBusy] = useState(false);
  const ai = item.ai || {};
  const rerun = async () => {
    setBusy(true);
    try { await API.post(`/inbox/${item.id}/reanalyse`); toast.success("AI read updated"); onChanged?.(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  if (!ai.summary && !ai.ai_classified) {
    return (
      <div className="flex items-center justify-between gap-2 rounded-md border border-dashed border-border px-3 py-2 text-[11px] text-muted-foreground" data-testid="inbox-ai-empty">
        <span className="inline-flex items-center gap-1"><Sparkle size={12} /> No AI summary yet{ai.error ? ` — ${ai.error.slice(0, 80)}` : ""}</span>
        <button type="button" onClick={rerun} disabled={busy} className="inline-flex items-center gap-1 text-primary hover:underline disabled:opacity-50" data-testid="inbox-ai-rerun"><ArrowsClockwise size={11} className={busy ? "animate-spin" : ""} /> {busy ? "Reading…" : "Summarise now"}</button>
      </div>
    );
  }
  return (
    <div className="rounded-md border border-violet-200 bg-violet-50/60 dark:border-violet-900 dark:bg-violet-950/20 px-3 py-2.5" data-testid="inbox-ai-box">
      <div className="flex items-start justify-between gap-2">
        <p className="text-sm text-foreground leading-snug flex items-start gap-1.5"><Sparkle size={14} weight="fill" className="text-violet-500 mt-0.5 shrink-0" /><span data-testid="inbox-ai-summary">{ai.summary || "—"}</span></p>
        <button type="button" onClick={rerun} disabled={busy} className="text-muted-foreground hover:text-foreground shrink-0" title="Re-run the AI read" data-testid="inbox-ai-rerun"><ArrowsClockwise size={13} className={busy ? "animate-spin" : ""} /></button>
      </div>
      <div className="flex items-center gap-1.5 mt-1.5 flex-wrap text-[10px]">
        {ai.category && <span className="px-1.5 py-0.5 rounded-full bg-white/70 dark:bg-background border border-violet-200 dark:border-violet-900 capitalize" data-testid="inbox-ai-category">Suggested: {ai.category.replace(/_/g, " ")}{ai.category_confidence ? ` · ${Math.round(ai.category_confidence * 100)}%` : ""}</span>}
        {ai.priority && <span className="px-1.5 py-0.5 rounded-full bg-white/70 dark:bg-background border border-violet-200 dark:border-violet-900 capitalize" data-testid="inbox-ai-priority">AI urgency: {ai.priority}{ai.priority_confidence ? ` · ${Math.round(ai.priority_confidence * 100)}%` : ""}</span>}
        {item.priority_source === "ai" && <span className="px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300 font-medium" data-testid="inbox-ai-raised">Priority raised by AI</span>}
      </div>
    </div>
  );
}

/** Replies sent from the inbox (as the mailbox) + the composer. */
export function ReplyThread({ item, smtpReady, onChanged }) {
  const [open, setOpen] = useState(false);
  const [body, setBody] = useState("");
  const [subject, setSubject] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [busy, setBusy] = useState(false);
  const replies = item.replies || [];
  const ref = item.job_request_ref || item.linked_ref || "";
  const defaultSubject = (() => {
    const s = (item.subject || "").trim();
    const re = /^re:\s/i.test(s) ? s : `Re: ${s}`;
    return ref && !re.toLowerCase().includes(ref.toLowerCase()) ? `${re} [${ref}]` : re;
  })();
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await API.post(`/inbox/${item.id}/reply`, { body, subject: subject || defaultSubject, attachments: attachments.map(a => ({ file_id: a.file_id, url: a.url, name: a.name })) });
      toast.success(`Reply sent to ${data.to}${data.attachments?.length ? ` with ${data.attachments.length} attachment${data.attachments.length === 1 ? "" : "s"}` : ""}`);
      setBody(""); setSubject(""); setAttachments([]); setOpen(false);
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-2" data-testid="inbox-reply-thread">
      {replies.map(r => (
        <div key={r.id} className="ml-6 rounded-md border border-sky-200 bg-sky-50/60 dark:border-sky-900 dark:bg-sky-950/20 px-3 py-2" data-testid={`inbox-reply-${r.id}`}>
          <p className="text-[10px] text-muted-foreground flex items-center gap-1 flex-wrap"><ArrowBendUpLeft size={10} /> {r.by_name || "Staff"} replied to {r.to} · {fmtWhen(r.sent_at)}
            {r.sent_copy?.saved && <span className="inline-flex items-center gap-0.5 text-emerald-700" title={`Copy saved to the mailbox's "${r.sent_copy.folder}" folder`} data-testid={`inbox-reply-sentcopy-${r.id}`}><CloudCheck size={10} weight="fill" /> in Sent</span>}
            {r.sent_copy?.skipped === "provider_keeps_copy" && <span className="text-muted-foreground" title="Gmail / Microsoft 365 keep their own copy of sent mail" data-testid={`inbox-reply-sentcopy-${r.id}`}>· provider keeps a copy</span>}
            {r.sent_copy && !r.sent_copy.saved && !r.sent_copy.skipped && <span className="text-amber-700" title={r.sent_copy.error || "Copy not saved"} data-testid={`inbox-reply-sentcopy-${r.id}`}>· not copied to Sent</span>}
          </p>
          <p className="text-[11px] font-medium mt-0.5">{r.subject}</p>
          <pre className="whitespace-pre-wrap font-sans text-xs mt-1">{r.body}</pre>
          {r.attachments?.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1.5" data-testid={`inbox-reply-attachments-${r.id}`}>
              {r.attachments.map((a, i) => (
                <a key={`${a.file_id || i}`} href={a.url || "#"} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded border border-sky-200 dark:border-sky-900 bg-white/70 dark:bg-background px-1.5 py-0.5 text-[10px] hover:underline"><Paperclip size={10} /> {a.name}</a>
              ))}
            </div>
          )}
        </div>
      ))}
      {open ? (
        <div className="rounded-md border border-border p-3 space-y-2 bg-card" data-testid="inbox-reply-composer">
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs text-muted-foreground">To <span className="font-medium text-foreground">{item.reply_to || item.from_email}</span> · sent from the connected mailbox</p>
            <button type="button" onClick={() => setOpen(false)} className="text-muted-foreground hover:text-foreground" aria-label="Close"><X size={14} /></button>
          </div>
          <Input value={subject || defaultSubject} onChange={e => setSubject(e.target.value)} className="h-8 text-xs" data-testid="inbox-reply-subject" />
          <ComposerAssist item={item} hasText={!!body.trim()} onInsert={(text) => setBody(text)} />
          <Textarea value={body} onChange={e => setBody(e.target.value)} rows={7} placeholder="Write your reply, use AI draft, or insert a saved reply… your name and company are added underneath." className="text-sm" autoFocus data-testid="inbox-reply-body" />
          <ReplyAttachments item={item} value={attachments} onChange={setAttachments} />
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button size="sm" onClick={send} disabled={busy || !body.trim()} data-testid="inbox-reply-send"><PaperPlaneTilt size={14} className="mr-1" /> {busy ? "Sending…" : "Send reply"}</Button>
          </div>
        </div>
      ) : (
        <Button size="sm" variant="outline" onClick={() => setOpen(true)} disabled={!smtpReady} title={smtpReady ? "Reply as the mailbox" : "Add the SMTP details in Mailbox settings to reply from here"} data-testid="inbox-reply-btn">
          <ArrowBendUpLeft size={14} className="mr-1" /> Reply{!smtpReady ? " (set up SMTP first)" : ""}
        </Button>
      )}
    </div>
  );
}
